import { goto } from '$app/navigation';
import { ApiService } from '$lib/services/api';
import {
	PAGES,
	GTM_EVENT_NAMES,
	COOKIES,
	COOKIES_CONSENT,
	LOCAL_STORAGE_KEYS,
	THIRD_PARTY_EVENTS,
	QUERY_PARAM_KEYS,
	AOFL_PRODUCT_NAMES,
	AOFL_PRODUCT_UUIDS
} from '$lib/utils/constants';
import { browser } from '$app/environment';
import { get } from 'svelte/store';
import { UnknownError } from '../subscription/modules/errors';
import { publishFailure, publishSuccess, user, resetStore } from './store';
import { startLoading, stopLoading } from '$lib/stores/app/actions';
import { Sentry } from '$lib/services/sentry';
import Cookies from 'js-cookie';
import Logger from '$lib/services/analytics/events';
import { EventType } from '$lib/services/analytics/events/common';
import { getDeviceId } from '$lib/utils/device-id';
import { app } from '../app/store';
import { AnalyticsService } from '$lib/services/analytics';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '$lib/services/app';
import { ProductExperienceService } from '$lib/services/product-experience';
import { has1_0Account, has2_0Account } from '$lib/utils/aofl-accounts';
import { postAuthenticationPathService } from '$lib/services/post-authentication-path';

/**
 * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object; playVoiceOver?: boolean}>} result
 */
export async function login(credentialInfo, options = { redirect: false }) {
	try {
		startLoading();
		const loginResp = await ApiService.login({
			username: credentialInfo.email,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		await AnalyticsService.dataLayerPush({
			eventName: GTM_EVENT_NAMES.LOGIN,
			email: credentialInfo?.email
		});

		const newUserState = {
			...loginResp,
			password: credentialInfo.password,
			token_valid: true
		};
		publishSuccess(newUserState);

		if (has2_0Account()) {
			console.log('Setting post-login product experience to 2.0');
			ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		} else {
			console.log('Setting post-login product experience to 1.0');
			ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE);
		}

		// Get the user object from the latest store state
		const getUser = get(user);

		if (options.logType) logEvent(options.logType, getUser);
		let playVoiceOver = false;
		if (options.redirect) {
			const has_1_0_account = has1_0Account();
			const has_2_0_account = has2_0Account();

			// Play the update voice over for mixed 1.0 and 2.0 users
			playVoiceOver = has_1_0_account && has_2_0_account;

			const path = postAuthenticationPathService.getRedirectPath(getUser);

			playVoiceOver = playVoiceOver && path === PAGES.INTERNAL.DOWNLOAD_APP;

			// @TODO: move to Logger once cleanup for that is done
			await trackLogin(getUser, path, 'success');

			// Redirect to the path
			await goto(path);
		}

		return { data: getUser, error: null, playVoiceOver: playVoiceOver };
	} catch (error) {
		// @TODO: move to Logger once cleanup for that is done

		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);

		// Get the user object from the latest store state
		const getUser = get(user);
		await trackLogin(getUser, '', 'error');

		return { data: getUser, error: er, playVoiceOver: false };
	} finally {
		stopLoading();
	}
}

/**
 * @param {{ username: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @returns {Promise<object | unknown>} result
 */
export async function checkUserStatusWithCredentials(credentialInfo) {
	try {
		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.username,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		const newUserState = { ...userStatusResp };
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: checkUserStatusWithCredentials Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Validates the user.
 * @param {{ force?: boolean,forceIntrospect?: boolean }} [options] - options to validate the user
 * @returns {Promise<import('./store').UserStoreState>} result
 */
export async function validateUser({ force = false, forceIntrospect = false } = {}) {
	if (!browser) return;

	const url = new URL(window?.location?.href);
	const token = url.searchParams.get(QUERY_PARAM_KEYS.TOKEN);
	const deepLink = url.searchParams.get(QUERY_PARAM_KEYS.DEEP_LINK);
	const productUuid = url.searchParams.get(QUERY_PARAM_KEYS.PRODUCT_UUID);

	// If we ever have a token, we need to force and do instrospect.
	if (token) {
		force = true;
		forceIntrospect = true;
	}

	// Checks for pure 2.0 free accounts
	const currentUser = get(user);
	if (
		currentUser.tokenValid &&
		Array.isArray(currentUser.aoflProducts) &&
		currentUser.aoflProducts.length > 0 &&
		!force
	) {
		return currentUser;
	}
	try {
		// TODO: move this to a transport, combine with datalayer push
		if ((token && deepLink) || forceIntrospect) {
			if (productUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0) {
				ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
			} else if (productUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE) {
				ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE);
			}

			await tokenIntrospect(token ?? '');
		}

		const response = await ApiService.validateLogin();

		const newUserState = {
			...response
		};
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: validateUser Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
		return get(user);
	}
}

/**
 * Validates the user from a token
 * @param {string} token - the token to validate
 * @returns {Promise<object | null>} the user state
 */
export async function validateUserFromToken(token) {
	try {
		await tokenIntrospect(token);
		const response = await ApiService.validateLogin();
		await AnalyticsService.trackClientDebug({
			message: 'Success: token_introspect-' + token
		});

		const newUserState = {
			...response
		};
		publishSuccess(newUserState);
		return response;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: validateUserFromToken Action'
			}
		});
		return null;
	}
}

/**
 * Exchanges a short-life token for a new long term token
 * @param {string} token - the token to introspect
 * @returns {Promise<object | null>} the introspect response
 */
async function tokenIntrospect(token) {
	try {
		const response = await ApiService.tokenIntrospect({
			token: token
		});
		await AnalyticsService.trackClientDebug({
			message: 'Success: token_introspect-' + token
		});
		return response;
	} catch (error) {
		await AnalyticsService.trackClientDebug({
			message: 'Fail: token_introspect-' + token
		});
		Sentry.captureException(error, {
			extra: {
				action: 'Error: tokenIntrospect Action'
			}
		});
		return null;
	}
}

/**
 * Resets the cookies and local storage
 * @returns {void}
 */
const resetCookiesAndLocalStorage = () => {
	const deviceIdBackup = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP);
	const experiments = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);

	Object.keys(Cookies.get()).forEach((cookieName) => {
		if (cookieName === COOKIES.DEVICE_ID) {
			return;
		}
		Cookies.remove(cookieName);
	});

	localStorage.clear();

	if (experiments) {
		localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, experiments);
	}
	if (deviceIdBackup) {
		localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP, deviceIdBackup);
	}

	resetStore();
};

/**
 * Logs out the user
 * @param {import('$lib/stores/app/types').AppStore} appStore - appStore
 * @param {{ redirect: boolean }} options - options
 * @returns {Promise<void>} result
 */
export async function logout({ meta }, options = { redirect: true }) {
	try {
		startLoading();
		resetCookiesAndLocalStorage();
		await ApiService.userLogout();
		if (meta.inApp) {
			const appCallService = new AppService();
			await appCallService.logout();
		}
		stopLoading();
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Logout Action'
			}
		});
	} finally {
		// TODO: consider removing if use cases arise that dont want to go to login
		stopLoading();
		if (options.redirect) await goto(PAGES.INTERNAL.LOGIN);
	}
}

/**
 * Submits the users email
 * @param {{ email: string; countryAbbrv: string; usernameType: string; phone?: any; }} registerInfo - credentials
 * @param {{ redirect: boolean }} options - options to handle this action
 * @returns {Promise<object | unknown>} response
 */
export const registerEmail = async (registerInfo, options = { redirect: false }) => {
	try {
		// TODO: finish all entries like types and campaign tag.
		const response = await ApiService.prospectRegisterEmail({
			email_address: registerInfo?.email,
			prospect_type: '',
			language_locale: navigator.language,
			campaign_source_tag: '',
			registration_type: registerInfo?.regType,
			subscription_status: '',
			phone_number: registerInfo?.phone
		});

		// TODO: handle storing email in sessionStorage
		sessionStorage.setItem('tmpEmail', registerInfo.email);
		const newUserState = { tmpEmail: registerInfo.email };
		publishSuccess(newUserState);

		// TODO: move this to a transport, combine with datalayer push
		const sourceInfoJson = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO) ?? '{}';
		const sourceInfo = JSON.parse(sourceInfoJson) || {};
		const conversionEventId = uuidv4();
		const eventSource = sourceInfo?.id ? [sourceInfo?.id] : [];
		await AnalyticsService.trackThirdPartyConversion({
			conversion_event_id: conversionEventId,
			country_code: registerInfo?.countryAbbrv,
			email: registerInfo?.email,
			event_info: {
				...(sourceInfo?.values ? sourceInfo?.values : {}),
				fbc: Cookies.get(COOKIES.FBC),
				fbp: Cookies.get(COOKIES.FBP),
				analytics_cookies_enabled: Cookies.get(COOKIES_CONSENT.ANALYTICS) === 'true',
				targeting_cookies_enabled: Cookies.get(COOKIES_CONSENT.TARGETING) === 'true'
			},
			event_source: eventSource,
			event_type: THIRD_PARTY_EVENTS.INITIATE_CHECKOUT
		});
		await AnalyticsService.dataLayerPush({
			event_id: conversionEventId,
			eventName: GTM_EVENT_NAMES.PROSPECT_REG_SUBMITTED,
			email: registerInfo?.email,
			espRecipientID: response.esp_recipient_id,
			fbc: Cookies.get(COOKIES.FBC),
			fbp: Cookies.get(COOKIES.FBP)
		});

		return response;
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: registerEmail Action'
			}
		});
		console.error(error);
	} finally {
		if (options.redirect) {
			goto(PAGES.INTERNAL.SUBSCRIPTION);
		}
	}
};

/**
 * @param {EventType} eventType - type of event
 * @param {import('./store').UserStoreState} userInfo - the user state
 * @returns {Promise<object>}
 */
async function logEvent(eventType, userInfo) {
	return await Logger.log({
		type: eventType,
		deviceId: getDeviceId(),
		legacyTrackCookie: Cookies.get(COOKIES.TRACK_COOKIE),
		meta: get(app).meta,
		aoflProductId: ProductExperienceService.AOFL_PRODUCT_UUID,
		detail: {
			udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
			adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			firstTimeLogin: userInfo.isFirstLogin,
			userToProductId: userInfo.userToProductUuid
		}
	});
}

/**
 * Resets the user's password.
 * @param {{ token: string; email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect?: boolean | string; logType?: EventType; logOutOfDevices?: boolean; aoflProductUuid?: string; }} options - options to handle this action. If `redirect` is `true`, navigates to `PAGES.INTERNAL.LOGIN`. If a `string`, navigates to that path.
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function userResetPassword(
	credentialInfo,
	{
		redirect = false,
		logOutOfDevices = false,
		aoflProductUuid = ProductExperienceService.AOFL_PRODUCT_UUID
	} = {}
) {
	try {
		startLoading();
		const response = await ApiService.userResetPassword(
			credentialInfo,
			logOutOfDevices,
			aoflProductUuid
		);

		if (redirect) {
			const destination = typeof redirect === 'string' ? redirect : PAGES.INTERNAL.LOGIN;
			await goto(destination);
		}
		return { data: response, error: undefined };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: userResetPassword Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * @param {object} params - The forgot password parameters
 * @param {string} params.email - The user's email address
 * @param {string} params.usernameType - The type of username
 * @param {string} params.countryAbbrv - The country abbreviation
 * @param {string} params.aoflProductUuid - The aofl product uuid
 * @returns {Promise<object>} - The API response
 */
export async function userForgotPassword({ email, usernameType, countryAbbrv, aoflProductUuid }) {
	try {
		startLoading();
		const response = await ApiService.userForgotPassword({
			username_type: usernameType,
			username: email,
			country_abbrv: countryAbbrv,
			aofl_product_uuid: aoflProductUuid
		});

		return response;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: userForgotPassword Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * Gets the user's latest payment information
 * @returns {Promise<{
 *   data: {
 *     isValidPaymentInfo: boolean,
 *     paymentUuid: string,
 *     paymentProvider: string,
 *     paymentProviderInfo: {
 *       ccLastFour: string,
 *       cardBrand: string,
 *       cardExpirationDate: string,
 *       paymentUuid: string
 *     }
 *   },
 *   error: Error | null
 * }>} Payment info or error
 */
export async function getPaymentInfo() {
	try {
		startLoading();
		const response = await ApiService.getPaymentInfo();

		const data = response.is_valid_payment_info
			? {
					isValidPaymentInfo: response.is_valid_payment_info,
					paymentUuid: response.payment_uuid,
					paymentProvider: response.payment_provider,
					paymentProviderInfo: {
						ccLastFour: response.payment_provider_info.cc_last_four,
						cardBrand: response.payment_provider_info.card_brand,
						cardExpirationDate: response.payment_provider_info.card_expiration_date,
						paymentUuid: response.payment_provider_info.payment_uuid
					}
				}
			: { isValidPaymentInfo: response.is_valid_payment_info };

		return { data, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Get Payment Info Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	} finally {
		stopLoading();
	}
}

/**
 * Updates user credentials (email and/or password).
 * @param {{ token: string; username?: string; new_password?: string; old_password?: string; product_uuid?: string; }} credentials - The credential update object
 * @returns {Promise<{data: object; error: Error | null}>} result
 */
export async function updateUserCredentials(credentials) {
	try {
		startLoading();
		const response = await ApiService.updateUserCredentials(credentials);

		// Update the user store with the new masked email if provided
		if (response?.masked_email) {
			const currentUser = get(user);
			publishSuccess({
				...currentUser,
				maskedEmail: response.masked_email
			});
		}

		return { data: response, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Update User Credentials Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * Validates a recovery token.
 * @param {string} token - The token to validate.
 * @returns {Promise<object>} A promise that resolves with the result of the validation.
 */
export async function validateRecoveryToken(token) {
	try {
		startLoading();
		const response = await ApiService.userValidateRecoveryToken(token);
		return { data: response, error: undefined };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: validateRecoveryToken Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * Gets the recipient email info.
 * @param {{ email_address: string; }} payload - The payload.
 * @returns {Promise<object>} A promise that resolves with the recipient email info.
 */
export async function getRecipientEmailInfo({ email_address }) {
	try {
		startLoading();
		const response = await ApiService.recipientGetEmailInfo({ email_address });
		return { data: response, error: undefined };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: getRecipientEmailInfo Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * @typedef {object} TokenCreateResponse
 * @property {string} [token] - The short-lived authentication token
 */

/**
 * Creates a short-lived token from an existing authenticated session.
 * @param {string} productUuid - The product uuid to create the token for.
 * @returns {Promise<{error: Error | null; data: TokenCreateResponse | null;}>} Token response or error
 */
export async function tokenCreate(productUuid) {
	try {
		const response = await ApiService.tokenCreate(productUuid);
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Token Create Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * @typedef {object} DeviceSessionResponse
 * @property {string} [token] - The authentication token
 * @property {string} [user_to_product_uuid] - The user to product UUID
 */

/**
 * Creates a user tied to device id for initial device authentication.
 * Used when no existing authentication is available.
 * @returns {Promise<{error: Error | null; data: DeviceSessionResponse | null;}>} Device session info or error
 */
export async function createDeviceUser() {
	try {
		const deviceId = getDeviceId();
		const response = await ApiService.createDeviceSession(deviceId);
		const validateLoginResponse = await ApiService.validateLogin(response.token);
		publishSuccess({ ...validateLoginResponse });
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Create Device Session Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * Adds credential to existing account.
 * @param {object} credentials - Credentials to add on account
 * @param {string} token - Token
 * @param {string} aoflProductUuid  - aoflProductUuid
 * @returns {Promise<object>} Add credentials result
 */
export async function addCredentials(credentials, token, aoflProductUuid) {
	return await ApiService.addCredentials(credentials, token, aoflProductUuid);
}

/**
 * Generates a short-lived for the target product
 * @param {string} productUuid - The product uuid to create the token for.
 * @returns {Promise<string>} The short lived token
 */
export async function generateShortLivedToken(productUuid) {
	try {
		const response = await ApiService.tokenCreate(productUuid);
		return response.token;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: generateShortLivedToken Action'
			}
		});
		return '';
	}
}

/**
 *
 * @param {import('./store').UserStoreState} userStore - The user store.
 * @param {string} path - The path to redirect to.
 * @param {string} status - The status of the login.
 */
async function trackLogin(userStore, path, status) {
	delete userStore.tmpEmail;
	delete userStore.tmpPhone;
	delete userStore.password;

	try {
		await AnalyticsService.trackClientDebug({
			event_type: 'login',
			event_info: {
				status: status,
				redirect_path: path,
				login_response: userStore
			}
		});
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Login Action - trackClientDebug',
				error: error
			}
		});
	}
}

/**
 * Adds credential to existing account.
 * @param {string} password - Password
 * @returns {Promise<object>} Upgrade request result or error
 */
export async function authenticatePassword(password) {
	try {
		startLoading();
		const response = await ApiService.authenticatePassword(password);
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: upgradeRequest Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * Adds credential to existing account.
 * @param {string} productUuid - Product uuid
 * @param {string} planHash - Plan hash
 * @returns {Promise<object>} Upgrade request result or error
 */
export async function hasSubscribed(productUuid, planHash) {
	try {
		startLoading();
		const response = await ApiService.hasSubscribed(productUuid, planHash);
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: upgradeRequest Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}
